import { useState } from "react";
import useSWR from "swr";

import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const fetcher = (url: string) => fetch(url, { credentials: "include" }).then((res) => res.json());

const GENRES = ["bluegrass", "americana", "folk", "jam"];
const REGIONS = ["CA", "TN", "CO", "NY"];

export function TrendwatchPanel() {
  const [genre, setGenre] = useState("bluegrass");
  const [region, setRegion] = useState("CA");
  const { data: genreData, isLoading: loadingGenre } = useSWR(`/marketing/trends/genre?g=${genre}`, fetcher);
  const { data: regionData, isLoading: loadingRegion } = useSWR(`/marketing/trends/region?state=${region}`, fetcher);

  return (
    <Card className="space-y-4 border-border/70 bg-background/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-foreground">Trendwatch</p>
          <p className="text-xs text-muted-foreground">Genre + region heatmap across TikTok / IG / YT.</p>
        </div>
        <div className="flex gap-2">
          <Select value={genre} onValueChange={setGenre}>
            <SelectTrigger className="w-36 bg-background/60 text-foreground">
              <SelectValue placeholder="Genre" />
            </SelectTrigger>
            <SelectContent className="bg-background/40 text-foreground">
              {GENRES.map((entry) => (
                <SelectItem key={entry} value={entry}>
                  {entry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-28 bg-background/60 text-foreground">
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent className="bg-background/40 text-foreground">
              {REGIONS.map((entry) => (
                <SelectItem key={entry} value={entry}>
                  {entry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <section>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Genre signals</p>
        {loadingGenre ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <div className="mt-2 space-y-2 text-sm text-foreground/80">
            {(genreData?.audio ?? []).map((entry: any) => (
              <div key={entry.title} className="rounded-xl border border-border/70 bg-background/40 p-2">
                <p className="font-semibold text-foreground">{entry.title}</p>
                <p className="text-xs text-muted-foreground">{entry.platform} · score {entry.trend_score}</p>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              {(genreData?.genres ?? []).map((tag: string) => (
                <Badge key={tag} className="bg-muted/60 text-foreground">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </section>

      <section>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Regional spikes</p>
        {loadingRegion ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <div className="mt-2 space-y-2 text-sm text-foreground/80">
            {(regionData?.cities ?? []).map((city: any) => (
              <div key={city.city} className="flex items-center justify-between rounded-xl border border-border/70 bg-background/40 p-2">
                <span>{city.city}</span>
                <span className="text-xs text-muted-foreground">heat {(city.heat * 100).toFixed(0)}%</span>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              {(regionData?.hashtags ?? []).map((tag: string) => (
                <Badge key={tag} variant="outline" className="border-border text-foreground/80">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </section>
    </Card>
  );
}
